// Copyright (c) 2016 Deere & Company

import React, {PropTypes} from 'react';
import {
    getRepairedDateErrorMessage,
    getVsrMessage,
    hasVsrErrorForClaimField,
    isRepairDateAfterFailureDate,
    isResponsibleAccountEditable,
    useAmountIndexToChange
} from '../../../shared/services/claim-shared-helper';
import {
    isPartNormal,
    isValidClaim
} from '../../services/claim-validation-helper';
import AmountOfUseField from '../../../shared/components/claim-fields/amount-of-use';
import ClaimAction from '@deere/cps-it.warranty-model-factory/lib/claim-action';
import ClaimRgaStatusIndicator from '../../../shared/components/claim-rga-status-indicator';
import Dealer from '@deere/cps-it.warranty-model-factory/lib/dealer';
import FormValidation from '../../state/form-validation';
import {FormattedMessage} from 'react-intl';
import KeyValuePair from '@deere/cps-it.warranty-model-factory/lib/key-value-pair';
import LabeledDateInput from '../../../shared/components/claim-fields/labeled-date-input';
import LabeledInput from '../../../shared/components/claim-fields/labeled-input';
import LabeledInputWithBlur from '../../../shared/components/claim-fields/labeled-input-with-blur';
import LabeledKeyValueDropDown from '../../../shared/components/claim-fields/labeled-key-value-dropdown';
import PartsClaim from '@deere/cps-it.warranty-model-factory/lib/claim-part';
import Product from '@deere/cps-it.warranty-model-factory/lib/product';
import ResponsibleAccounts from '../../../shared/components/claim-fields/responsible-accounts';
import {fixCursorPosition} from '../../../../shared/services/fix-cursor-position';
import {formatPin} from '../../../../shared/services/format-pin';
import {getPartTypeSelectedValue} from '../../services/part-types';
import {getPartTypesCollection} from '../../services/get-part-types';
import {isValidDate} from '../../../../shared/services/build-date';
import moment from 'moment';

function resetCustomerProductDetails() {

    this.actions.resetPartsProductDetails();
    this.actions.resetCustomerDetails();
    this.actions.resetCkcCustomerFailure();

}

function handlePinBlur(pin) {

    if (pin) {

        if (pin !== this.product.pin) {

            this::resetCustomerProductDetails();
            this.actions.readClaimProduct(pin, true);

        }

    } else {

        this::resetCustomerProductDetails();

    }

}

function handleInputBlur(property, value) {

    this.actions.updatePartsClaimInputOnBlur(property, value, this.claim);

}

function handleResponsibleAccountChange(accountId) {

    this.actions.updateResponsibleAccountsDropDown(accountId, this.selectedAccounts, this.claim.pin);

}

function handleUseAmountChange(amount, index) {

    const indexToChange = useAmountIndexToChange(this.product.complianceApplies, index);

    this.actions.updatePartsClaimUseAmount(amount, indexToChange);

}

function handleUnitOptionChange(key) {

    this.actions.updatePartsClaimUnitDropDown(key, this.product.useAmountAllowedUnits);

}

function handleWorkOrderOneChange(workOrder) {

    this.actions.updatePartsClaimWorkOrderOne(workOrder);

}

function handleFailurePartChange(partNumber) {

    this.actions.updatePartsClaimFailurePart(partNumber);

}

function handlePartTypeChange(event) {

    this.actions.resetCustomerDetails();
    this.actions.resetCkcCustomerFailure();
    this.actions.updatePartType(event.target.value);

}

function handleFailureDateChange(date) {

    this.actions.updatePartsClaimFailureDate(date);

}

function handleRepairDateChange(date) {

    this.actions.updatePartsClaimRepairDate(date);

}

function handleButtonClick() {

    const action = new ClaimAction({
        actionName: 'VALIDATE',
        id: 'VALIDATE',
        mode: ''
    });

    this.actions.updateSelectedAction(action);

}

function handleOwnershipStatusChange(event) {

    this.actions.updateOwnershipStatusDropDown(event.target.value, this.ownershipStatus);

}

function handleDropDownBlur(property, event) {

    this.actions.updatePartsClaimInputOnBlur(property, event.target.value, this.claim);

}

function handleDateBlur(property, date) {

    const modifiedDate = isValidDate(date.target.value) ? moment.utc(date.target.value).valueOf() : null;

    this.actions.updatePartsClaimInputOnBlur(property, modifiedDate, this.claim);

}

function handleWorkOrderTwoChange(workOrder) {

    this.actions.updatePartsClaimWorkOrderTwo(workOrder);

}

function handleRepairNumberChange(repairNumber) {

    this.actions.updatePartsClaimRepairNumber(repairNumber);

}

function handleClaimPropertyChange(property, value) {

    this.actions.updatePartsClaimProperty(property, value);

}

function handlePinChange(pin, event) {

    const formattedPin = formatPin(pin);

    this.actions.updatePartsClaimPin(formattedPin);
    fixCursorPosition(event, formattedPin);

}

const pinClassName = claim => `pin ${claim.model && claim.serialNumber ? '' : 'claim-required'}`;
const modelClassName = claim => `model ${claim.pin ? '' : 'claim-required'}`;
const serialNumberClassName = claim => `serial-number ${claim.pin ? '' : 'claim-required'}`;
const previousInvoiceClassName = claim => `previous-invoice-number ${isPartNormal(claim) ? 'claim-required' : ''}`;
const ownershipStatusClassName = claim => `ownership-status ${isPartNormal(claim) ? 'claim-required' : ''}`;
const retailInstallDateClassName = claim => `retail-install-date ${claim.lifeTime ? '' : 'claim-required'}`;

const ClaimDetailsContainer = props => {

    const canEditResponsibleAccount = isResponsibleAccountEditable(props.isEditable, props.claim.claimStatus);

    return (
        <section className='claim-details-section'>
            <div className='claim-details-section-header'>
                <h3>
                    <FormattedMessage id='PRODUCT_INFORMATION'/>
                </h3>
                {props.claim.rgaStatus.status && props.claim.rgaStatus.status !== 'NONE' &&
                    <ClaimRgaStatusIndicator
                        claim={props.claim.claimNumber}
                        claimRgaStatus={props.claim.rgaStatus}
                    />
                }
            </div>
            <div className='product-info'>
                <ResponsibleAccounts
                    dealers={props.selectedAccounts}
                    errorMessage='DEALER_LOCATION_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'responsibleAccount', canEditResponsibleAccount)}
                    isEditable={canEditResponsibleAccount}
                    isValid={props.formValidation.responsibleAccount}
                    onOptionBlur={handleInputBlur.bind(props, 'responsibleAccount')}
                    onOptionChange={props::handleResponsibleAccountChange}
                    selectedAccount={props.claim.responsibleAccount}
                    vsrMessage={getVsrMessage(props.claim.errors, 'responsibleAccount', canEditResponsibleAccount)}
                />
                <LabeledKeyValueDropDown
                    className='part-type'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'partType', props.isEditable)}
                    isEditable={props.isEditable}
                    label='PART_TYPE'
                    list={getPartTypesCollection()}
                    onChange={props::handlePartTypeChange}
                    placeholder='SERVICE_PART'
                    selectedValue={getPartTypeSelectedValue(props.claim.lifeTime, props.claim.merchandizeItem)}
                    shouldTranslate={false}
                    showBlankOption={false}
                    showPlaceholder={true}
                    vsrMessage={getVsrMessage(props.claim.errors, 'partType', props.isEditable)}
                />
            </div>
            {isPartNormal(props.claim) &&
                <div className='parts-info'>
                    <LabeledInputWithBlur
                        className={pinClassName(props.claim)}
                        errorMessage='PIN_IS_REQUIRED'
                        highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'pin', props.isEditable)}
                        isEditable={props.isEditable && (props.claim.claimStatus.key === 'ZDFT' || !props.claim.claimStatus.key)}
                        isValid={props.formValidation.pin}
                        label='PIN'
                        maxLength={18}
                        onBlur={props::handlePinBlur}
                        onChange={props::handlePinChange}
                        value={props.claim.pin}
                        vsrMessage={getVsrMessage(props.claim.errors, 'pin', props.isEditable)}
                    />
                    <div className='replacement-info'>
                        <LabeledInput
                            className={modelClassName(props.claim)}
                            highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'model', props.isEditable)}
                            isEditable={props.isEditable}
                            label='MODEL'
                            maxLength={20}
                            onChange={handleClaimPropertyChange.bind(props, 'model')}
                            value={props.claim.model}
                            vsrMessage={getVsrMessage(props.claim.errors, 'model', props.isEditable)}
                        />
                        <LabeledInput
                            className={serialNumberClassName(props.claim)}
                            highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'serialNumber', props.isEditable)}
                            isEditable={props.isEditable}
                            label='SERIAL_NUMBER'
                            maxLength={6}
                            onChange={handleClaimPropertyChange.bind(props, 'serialNumber')}
                            value={props.claim.serialNumber}
                            vsrMessage={getVsrMessage(props.claim.errors, 'serialNumber', props.isEditable)}
                        />
                    </div>
                </div>
            }
            <h3>
                <FormattedMessage id='REFERENCE_INFORMATION'/>
            </h3>
            <div className='reference-info'>
                <LabeledInputWithBlur
                    className='work-order-one'
                    errorMessage='WORK_ORDER_ONE_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'workOrderOne', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.workOrderOne}
                    label='WORK_ORDER_ONE'
                    maxLength={26}
                    onBlur={handleInputBlur.bind(props, 'workOrderOne')}
                    onChange={props::handleWorkOrderOneChange}
                    value={props.claim.referenceInfo.referenceNumber1}
                    vsrMessage={getVsrMessage(props.claim.errors, 'workOrderOne', props.isEditable)}
                />
                <LabeledInput
                    className='work-order-two'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'workOrderTwo', props.isEditable)}
                    isEditable={props.isEditable}
                    label='WORK_ORDER_TWO'
                    maxLength={12}
                    onChange={props::handleWorkOrderTwoChange}
                    value={props.claim.referenceInfo.referenceNumber2}
                    vsrMessage={getVsrMessage(props.claim.errors, 'workOrderTwo', props.isEditable)}
                />
                <LabeledInput
                    className='repair-number'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'repairNumber', props.isEditable)}
                    isEditable={props.isEditable}
                    label='REPAIR_NUMBER'
                    maxLength={13}
                    onChange={props::handleRepairNumberChange}
                    value={props.claim.referenceInfo.repairNumber}
                    vsrMessage={getVsrMessage(props.claim.errors, 'repairNumber', props.isEditable)}
                />
                <LabeledKeyValueDropDown
                    className={ownershipStatusClassName(props.claim)}
                    errorMessage='OWNERSHIP_STATUS_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'ownershipStatus', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.ownershipStatus}
                    label='OWNERSHIP_STATUS'
                    list={props.ownershipStatus}
                    onBlur={handleDropDownBlur.bind(props, 'ownershipStatus')}
                    onChange={props::handleOwnershipStatusChange}
                    selectedValue={props.claim.referenceInfo.ownershipStatus}
                    vsrMessage={getVsrMessage(props.claim.errors, 'ownershipStatus', props.isEditable)}
                />
            </div>
            <div className='replacement-fields'>
                <LabeledInputWithBlur
                    className={previousInvoiceClassName(props.claim)}
                    errorMessage='PREVIOUS_INVOICE_NUMBER_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'previousInvoiceNumber', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.previousInvoiceNumber}
                    label='PREVIOUS_INVOICE_NUMBER'
                    maxLength={15}
                    onBlur={handleInputBlur.bind(props, 'previousInvoiceNumber')}
                    onChange={handleClaimPropertyChange.bind(props, 'previousInvoiceNumber')}
                    value={props.claim.previousInvoiceNumber}
                    vsrMessage={getVsrMessage(props.claim.errors, 'previousInvoiceNumber', props.isEditable)}
                />
                <LabeledDateInput
                    className={retailInstallDateClassName(props.claim)}
                    date={props.claim.retailInstallDate}
                    errorMessage='RETAIL_INSTALL_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'retailInstallDate', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.retailInstallDate}
                    label='RETAIL_INSTALL'
                    maxDate={moment()}
                    onBlur={handleDateBlur.bind(props, 'retailInstallDate')}
                    onDateChange={handleClaimPropertyChange.bind(props, 'retailInstallDate')}
                    vsrMessage={getVsrMessage(props.claim.errors, 'retailInstallDate', props.isEditable)}
                />
            </div>
            <div className='failure-info'>
                <LabeledDateInput
                    className='failure-date'
                    date={props.claim.failureInfo.failureDate}
                    errorMessage='FAILED_DATE_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'failureDate', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.failureDate}
                    label='FAILED'
                    maxDate={moment()}
                    onBlur={handleDateBlur.bind(props, 'failureDate')}
                    onDateChange={props::handleFailureDateChange}
                    vsrMessage={getVsrMessage(props.claim.errors, 'failureDate', props.isEditable)}
                />
                <LabeledDateInput
                    className='repair-date'
                    date={props.claim.failureInfo.repairDate}
                    errorMessage={getRepairedDateErrorMessage(props.claim.failureInfo.repairDate)}
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'repairDate', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.repairDate && isRepairDateAfterFailureDate(props.claim.failureInfo.repairDate, props.claim.failureInfo.failureDate)}
                    label='REPAIRED'
                    maxDate={moment()}
                    onBlur={handleDateBlur.bind(props, 'repairDate')}
                    onDateChange={props::handleRepairDateChange}
                    vsrMessage={getVsrMessage(props.claim.errors, 'repairDate', props.isEditable)}
                />
                {isPartNormal(props.claim) && props.product.useAmountAllowedUnits.length > 0 &&
                    <AmountOfUseField
                        allowedUnits={props.product.useAmountAllowedUnits}
                        complianceApplies={props.product.complianceApplies}
                        errors={props.claim.errors}
                        formValidation={props.formValidation}
                        isEditable={props.isEditable}
                        onInputBlur={handleInputBlur.bind(props, 'useAmount')}
                        onInputChange={props::handleUseAmountChange}
                        onOptionBlur={handleInputBlur.bind(props, 'useAmountUnit')}
                        onOptionChange={props::handleUnitOptionChange}
                        useAmounts={props.claim.useAmounts}
                    />
                }
                <LabeledInputWithBlur
                    className='failure-part'
                    errorMessage='FAILED_PART_IS_REQUIRED'
                    highlightClass={hasVsrErrorForClaimField(props.claim.errors, 'failurePart', props.isEditable)}
                    isEditable={props.isEditable}
                    isValid={props.formValidation.failurePart}
                    label='FAILED_PART'
                    maxLength={18}
                    onBlur={handleInputBlur.bind(props, 'failurePart')}
                    onChange={props::handleFailurePartChange}
                    value={props.claim.failureInfo.failurePartNumber}
                    vsrMessage={getVsrMessage(props.claim.errors, 'failurePart', props.isEditable)}
                />
            </div>
            {props.isEditable && props.claim.showValidate &&
                <div className='button-group'>
                    <button
                        disabled={!isValidClaim(props.claim, props.product.useAmountAllowedUnits.length)}
                        onClick={props::handleButtonClick}
                    >
                        <FormattedMessage id='VALIDATE'/>
                    </button>
                </div>
            }
        </section>
    );

};

ClaimDetailsContainer.displayName = 'ClaimDetailsContainer';
ClaimDetailsContainer.propTypes = {
    actions: PropTypes.object.isRequired,
    claim: PropTypes.instanceOf(PartsClaim).isRequired,
    formValidation: PropTypes.instanceOf(FormValidation).isRequired,
    isEditable: PropTypes.bool.isRequired,
    isValidClaim: PropTypes.bool.isRequired,
    ownershipStatus: PropTypes.arrayOf(PropTypes.instanceOf(KeyValuePair)).isRequired,
    product: PropTypes.instanceOf(Product).isRequired,
    selectedAccounts: PropTypes.arrayOf(PropTypes.instanceOf(Dealer)).isRequired
};

export default ClaimDetailsContainer;



// WEBPACK FOOTER //
// ./src/claim/parts/components/claim-details-section/claim-details-container.js